import { Link } from "react-router-dom";
import { formatIDR, fileUrl } from "../lib/api";
import StockPill from "./StockPill";
import { formatProductName } from "../lib/productName";
import JubelioBadge from "./JubelioBadge";
import { ShareMenu, CopySkuButton } from "./ProductActions";
import { useAuth } from "../context/AuthContext";
import { ReviewBadge, REVIEW_EDITOR_ROLES } from "../lib/review";
import { shopeeUrl, ShopeeLogo } from "../lib/marketplace";

export default function ProductCard({ product, regionBadge = null }) {
    const { user } = useAuth();
    const showReview = REVIEW_EDITOR_ROLES.includes(user?.role);
    const main = product.images?.find((i) => i.is_main) || product.images?.[0];
    const hasVariants = product.variants?.length > 0;
    const availableVariants = hasVariants
        ? product.variants.filter((v) => (Number(v.stock) || 0) > 0).length
        : 0;
    const variantSummary = hasVariants
        ? `${availableVariants}/${product.variants.length} variants available`
        : product.carat
          ? product.carat
          : product.size
            ? `Size ${product.size}`
            : null;

    const prices = hasVariants
        ? product.variants.map((v) => Number(v.retail_price) || 0).filter(Boolean)
        : [];
    const minP = prices.length ? Math.min(...prices) : product.retail_price;
    const maxP = prices.length ? Math.max(...prices) : product.retail_price;
    const priceLabel =
        hasVariants && minP !== maxP
            ? `${formatIDR(minP)} – ${formatIDR(maxP)}`
            : formatIDR(product.retail_price || minP);

    const totalStock = hasVariants
        ? product.variants.reduce((s, v) => s + (Number(v.stock) || 0), 0)
        : product.stock;
    const shopee = shopeeUrl(product.shopee_url);

    return (
        <div
            className="group relative flex flex-col border border-mist bg-white rounded-sm overflow-hidden hover:border-sage-dark transition-colors duration-200"
            data-testid={`product-card-${product.sku}`}
        >
            <Link to={`/products/${product.id}`} className="flex flex-col flex-1">
                <div className="aspect-square bg-[#F6F5F2] overflow-hidden relative flex items-center justify-center p-3">
                    {main ? (
                        <img
                            src={fileUrl(main.path)}
                            alt={product.name}
                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs uppercase tracking-widest">
                            No photo
                        </div>
                    )}
                    <div className="absolute top-3 left-3">
                        <StockPill status={product.stock_status} />
                    </div>
                    {regionBadge && (
                        <span
                            className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest border border-mist shadow-sm"
                            data-testid={`card-region-${regionBadge.slug}`}
                        >
                            <span>{regionBadge.icon}</span>
                            <span className="text-neutral-700">{regionBadge.name}</span>
                        </span>
                    )}
                </div>
                <div className="p-4 md:p-5 space-y-2 flex-1 flex flex-col">
                    <div className="flex items-center justify-between gap-2">
                        <div className="eyebrow text-[10px] truncate">{product.category}</div>
                        {product.status && product.status !== "active" && (
                            <span className="text-[9px] uppercase tracking-widest text-neutral-400 border border-mist px-1.5 py-0.5 rounded-full">
                                {product.status === "draft" ? "Draft" : "Archived"}
                            </span>
                        )}
                    </div>
                    <h3 data-testid={`product-card-title-${product.sku}`} className="font-sans font-medium text-[15px] md:text-[17px] leading-[1.3] min-h-[2.6em] line-clamp-2">
                        {formatProductName(product.name)}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs min-w-0 font-sans">
                        <span className="font-mono text-neutral-700 truncate">{product.sku}</span>
                        <CopySkuButton sku={product.sku} />
                        <JubelioBadge product={product} size={11} />
                        {shopee && (
                            <button type="button" aria-label="Open Shopee listing" title="Open Shopee listing"
                                data-testid={`product-card-shopee-${product.sku}`}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(shopee, "_blank", "noopener,noreferrer"); }}
                                className="shrink-0 hover:opacity-80 transition-opacity">
                                <ShopeeLogo size={14} />
                            </button>
                        )}
                        {showReview && <ReviewBadge product={product} compact testId={`card-review-${product.sku}`} />}
                    </div>
                    {variantSummary && <p className="text-xs text-neutral-500 line-clamp-1 font-sans">{variantSummary}</p>}
                    <div className="flex-1 !mt-0" />
                    <div className="pt-3 border-t border-mist flex items-end justify-between gap-3 font-sans">
                        <div className="min-w-0">
                            <div className="eyebrow text-[10px]">Price</div>
                            <div className="text-sm md:text-base font-medium text-ink truncate tabular-nums">{priceLabel}</div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="eyebrow text-[10px]">Stock</div>
                            <div className="text-sm md:text-base font-medium text-ink tabular-nums">{totalStock}</div>
                        </div>
                    </div>
                </div>
            </Link>
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-1.5 py-1 border border-mist">
                <ShareMenu product={product} iconOnly />
            </div>
        </div>
    );
}
